// @flow

import * as React from 'react';
import { graphql, useFragment } from 'react-relay';
import { Flex, Box, useSystem } from 'react-system';
import { CheckMark } from '../../icons/check-mark';
import { useTheme } from '../../theme';
import { useLocale } from '../../hooks/locale';
import type { AggregatesListingCharacteristics_listing$key } from './__generated__/AggregatesListingCharacteristics_listing.graphql';

type Props = {|
  listing: AggregatesListingCharacteristics_listing$key,
|};

export const AggregatesListingCharacteristics = (props: Props): React.Node => {
  const { text, colors } = useTheme();
  const { mr, media } = useSystem();
  const { t, numberLocale } = useLocale();

  const listing = useFragment(
    graphql`
      fragment AggregatesListingCharacteristics_listing on AggregatesListing {
        hasBalcony
        hasGarden
        hasParking
        hasElevator
        hasView
        hasFireplace
        hasGarage
        isChildFriendly
        isWheelchairAccessible
        isMinergieCertified
        isNewDevelopment
        hasLavatoryConnection
        hasGasSupply
        hasSewerageSupply
        hasCellar
        hasWaterSupply
        hasStorageRoom
        hasDishWasher
        hasPlayGround
        hasVirtualTour
        hasRestrooms
        hasCableTv
        hasWashingMachine
        hasTumbleDryer
        hasInternet
        isGroundfloorRaised
        isHighlighted
        isAnimalFriendly
        isOldBuilding
        secondaryResidenceEligible
        hasNewBuildingProject
        distPublicTransportation
        distShopping
        distKindergarten
        distElementarySchool
        distHighSchool
        distMotorWay
      }
    `,
    props.listing,
  );

  const {
    hasBalcony,
    hasGarden,
    hasParking,
    hasElevator,
    hasView,
    hasFireplace,
    hasGarage,
    isChildFriendly,
    isWheelchairAccessible,
    isMinergieCertified,
    isNewDevelopment,
    hasLavatoryConnection,
    hasGasSupply,
    hasSewerageSupply,
    hasCellar,
    hasWaterSupply,
    hasStorageRoom,
    hasDishWasher,
    hasPlayGround,
    hasVirtualTour,
    hasRestrooms,
    hasCableTv,
    hasWashingMachine,
    hasTumbleDryer,
    hasInternet,
    isGroundfloorRaised,
    isHighlighted,
    isAnimalFriendly,
    isOldBuilding,
    secondaryResidenceEligible,
    hasNewBuildingProject,
    distPublicTransportation,
    distShopping,
    distKindergarten,
    distElementarySchool,
    distHighSchool,
    distMotorWay,
  } = listing;

  const distances = [
    {
      label: t('distPublicTransportation'),
      value: distPublicTransportation,
    },
    { label: t('distShopping'), value: distShopping },
    { label: t('distKindergarten'), value: distKindergarten },
    { label: t('distElementarySchool'), value: distElementarySchool },
    { label: t('distHighSchool'), value: distHighSchool },
    { label: t('distMotorWay'), value: distMotorWay },
  ].filter(item => item.value != null);

  const characteristics = [
    { label: t('hasBalconyAuto'), value: hasBalcony },
    { label: t('hasGardenAuto'), value: hasGarden },
    { label: t('hasParkingAuto'), value: hasParking },
    { label: t('hasElevator'), value: hasElevator },
    { label: t('hasView'), value: hasView },
    { label: t('hasFireplace'), value: hasFireplace },
    { label: t('hasGarage'), value: hasGarage },
    { label: t('childFriendly'), value: isChildFriendly },
    { label: t('wheelChairAccessible'), value: isWheelchairAccessible },
    { label: t('minergieCertified'), value: isMinergieCertified },
    { label: t('newDevelopment'), value: isNewDevelopment },
    { label: t('hasLavatoryConnection'), value: hasLavatoryConnection },
    { label: t('hasGasSupply'), value: hasGasSupply },
    { label: t('hasSewerageSupply'), value: hasSewerageSupply },
    { label: t('hasCellar'), value: hasCellar },
    { label: t('hasWaterSupply'), value: hasWaterSupply },
    { label: t('hasStorageRoom'), value: hasStorageRoom },
    { label: t('hasDishWasher'), value: hasDishWasher },
    { label: t('hasPlayGround'), value: hasPlayGround },
    { label: t('hasVirtualTour'), value: hasVirtualTour },
    { label: t('hasRestrooms'), value: hasRestrooms },
    { label: t('hasCableTv'), value: hasCableTv },
    { label: t('hasWashingMachine'), value: hasWashingMachine },
    { label: t('hasTumbleDryer'), value: hasTumbleDryer },
    { label: t('hasInternet'), value: hasInternet },
    { label: t('isGroundfloorRaised'), value: isGroundfloorRaised },
    { label: t('isHighlighted'), value: isHighlighted },
    { label: t('isAnimalFriendly'), value: isAnimalFriendly },
    { label: t('isOldBuilding'), value: isOldBuilding },
    {
      label: t('secondaryResidenceEligible'),
      value: secondaryResidenceEligible,
    },
    {
      label: t('hasNewBuildingProject'),
      value: hasNewBuildingProject,
    },
  ].filter(item => item.value === true);

  const renderCharacteristic = characteristic => {
    return (
      <Flex flexWrap="wrap" mb={3}>
        <CheckMark css={mr(3)} size={25} fill={colors.cyan700} />
        <Box css={text.body1}>{characteristic.label}</Box>
      </Flex>
    );
  };

  const renderDistance = distance => {
    const rawDistance = distance.value ?? 0;

    return (
      rawDistance > 0 && (
        <Flex flexWrap="wrap" mb={3}>
          <CheckMark css={mr(3)} size={25} fill={colors.cyan700} />
          <Box css={text.body1}>{`${distance.label} -
                  ${numberLocale.format(rawDistance / 1000, {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 2,
                  })} km`}</Box>
        </Flex>
      )
    );
  };

  return (
    <>
      {characteristics.length > 0 && (
        <Box pr={[0, 2]} mb={4}>
          <Box as="h3" py={2} css={media([text.h6, text.h5])}>
            {t('equipmentAndCharacteristics')}
          </Box>
          <Flex flexWrap="wrap">
            {characteristics.map((characteristic, index) => (
              <Box key={index} width={[1, 1 / 2]}>
                {renderCharacteristic(characteristic)}
              </Box>
            ))}
          </Flex>
        </Box>
      )}
      {distances.length > 0 && (
        <Box pr={[0, 2]} mb={4}>
          <Box py={3} css={text.h5}>
            {t('pointsOfInterestNearby')}
          </Box>
          <Flex flexWrap="wrap">
            {distances.map((distance, index) => (
              <Box key={index} width={[1, 1 / 2]}>
                {renderDistance(distance)}
              </Box>
            ))}
          </Flex>
        </Box>
      )}
    </>
  );
};
